import {
  FieldType,
  FormulaFieldType,
} from '@grouper/modules/database/fieldTypes'

import GridViewFieldAI from '@grouper_premium/components/views/grid/fields/GridViewFieldAI'
import FunctionalGridViewFieldAI from '@grouper_premium/components/views/grid/fields/FunctionalGridViewFieldAI'
import RowEditFieldAI from '@grouper_premium/components/row/RowEditFieldAI'
import FieldAISubForm from '@grouper_premium/components/field/FieldAISubForm'
import FormulaFieldAI from '@grouper_premium/components/field/FormulaFieldAI'
import GridViewFieldAIGenerateValuesContextItem from '@grouper_premium/components/views/grid/fields/GridViewFieldAIGenerateValuesContextItem'
import PremiumFeatures from '@grouper_premium/features'
import PaidFeaturesModal from '@grouper_premium/components/PaidFeaturesModal'
import { AIPaidFeature } from '@grouper_premium/paidFeatures'
import _ from 'lodash'
import WorkspaceSettingsModal from '@grouper/modules/core/components/workspace/WorkspaceSettingsModal.vue'

export class AIFieldType extends FieldType {
  static getType() {
    return 'ai'
  }

  getIconClass() {
    return 'iconoir-magic-wand'
  }

  getName() {
    const { $i18n } = this.app
    return $i18n.t('premiumFieldType.ai')
  }

  isReadOnlyField(field) {
    if (field && _.isBoolean(field.read_only)) {
      return field.read_only
    }
    return false
  }

  canBeDefaultValue() {
    return false
  }

  getGridViewFieldComponent() {
    return GridViewFieldAI
  }

  getFunctionalGridViewFieldComponent() {
    return FunctionalGridViewFieldAI
  }

  getRowEditFieldComponent(field) {
    return RowEditFieldAI
  }

  getFormComponent() {
    return FieldAISubForm
  }

  getGrouperFieldType(field) {
    return this.app.$registry
      .get('aiFieldOutputType', field.ai_output_type)
      .getGrouperFieldType()
  }

  getValidationError(field, value) {
    return this.getGrouperFieldType(field).getValidationError(field, value)
  }

  getCardComponent(field) {
    return this.getGrouperFieldType(field).getCardComponent(field)
  }

  getRowHistoryEntryComponent(field) {
    return null
  }

  getFormViewFieldComponents(field) {
    return {}
  }

  getDefaultValue(field) {
    return null
  }

  getCardValueHeight(field) {
    return this.getGrouperFieldType(field).getCardValueHeight(field)
  }

  getSort(name, order, field) {
    return this.getGrouperFieldType(field).getSort(name, order, field)
  }

  getCanSortInView(field) {
    return this.getGrouperFieldType(field).getCanSortInView(field)
  }

  getDocsDataType(field) {
    return this.getGrouperFieldType(field).getDocsDataType(field)
  }

  getDocsDescription(field) {
    const { $i18n } = this.app
    return $i18n.t('premiumFieldType.aiDescription')
  }

  getDocsRequestExample(field) {
    return 'read only'
  }

  getDocsResponseExample(field) {
    return this.getGrouperFieldType(field).getDocsResponseExample(field)
  }

  prepareValueForCopy(field, value) {
    return this.getGrouperFieldType(field).prepareValueForCopy(field, value)
  }

  getContainsFilterFunction(field) {
    return this.getGrouperFieldType(field).getContainsFilterFunction(field)
  }

  getContainsWordFilterFunction(field) {
    return this.getGrouperFieldType(field).getContainsWordFilterFunction(field)
  }

  getStartsWithFilterFunction(field) {
    return this.getGrouperFieldType(field).getStartsWithFilterFunction(field)
  }

  toHumanReadableString(field, value) {
    return this.getGrouperFieldType(field).toHumanReadableString(field, value)
  }

  getSortIndicator(field) {
    return this.getGrouperFieldType(field).getSortIndicator(field)
  }

  canRepresentDate(field) {
    return this.getGrouperFieldType(field).canRepresentDate(field)
  }

  getCanGroupByInView(field) {
    return this.getGrouperFieldType(field).getCanGroupByInView(field)
  }

  parseInputValue(field, value) {
    return this.getGrouperFieldType(field).parseInputValue(field, value)
  }

  canRepresentFiles(field) {
    return this.getGrouperFieldType(field).canRepresentFiles(field)
  }

  getHasEmptyValueFilterFunction(field) {
    return this.getGrouperFieldType(field).getHasEmptyValueFilterFunction(field)
  }

  getHasValueContainsFilterFunction(field) {
    return this.getGrouperFieldType(field).getHasValueContainsFilterFunction(
      field
    )
  }

  getHasValueContainsWordFilterFunction(field) {
    return this.getGrouperFieldType(
      field
    ).getHasValueContainsWordFilterFunction(field)
  }

  getHasValueLengthIsLowerThanFilterFunction(field) {
    return this.getGrouperFieldType(
      field
    ).getHasValueLengthIsLowerThanFilterFunction(field)
  }

  getGroupByComponent(field) {
    return this.getGrouperFieldType(field).getGroupByComponent(field)
  }

  getRowValueFromGroupValue(field, value) {
    return this.getGrouperFieldType(field).getRowValueFromGroupValue(
      field,
      value
    )
  }

  getGroupValueFromRowValue(field, value) {
    return this.getGrouperFieldType(field).getGroupValueFromRowValue(
      field,
      value
    )
  }

  isEqual(field, value1, value2) {
    return this.getGrouperFieldType(field).isEqual(field, value1, value2)
  }

  parseFilterValue(field, filterValue) {
    return this.getGrouperFieldType(field).parseFilterValue(field, filterValue)
  }

  formatFilterValue(field, value) {
    return this.getGrouperFieldType(field).formatFilterValue(field, value)
  }

  canBeReferencedByFormulaField(field) {
    return this.getGrouperFieldType(field).canBeReferencedByFormulaField(field)
  }

  getGridViewContextItemsOnCellsSelection(field) {
    return [GridViewFieldAIGenerateValuesContextItem]
  }

  isEnabled(workspace) {
    return Object.values(workspace.generative_ai_models_enabled).some(
      (models) => models.length > 0
    )
  }

  getDisabledClickModal(workspace) {
    return [WorkspaceSettingsModal, { workspace }]
  }

  getDisabledTooltip() {
    return 'Click to configure API key'
  }

  isDeactivated(workspaceId) {
    return !this.app.$hasFeature(PremiumFeatures.PREMIUM, workspaceId)
  }

  getDeactivatedClickModal(workspaceId) {
    return [
      PaidFeaturesModal,
      { 'initial-selected-type': AIPaidFeature.getType() },
    ]
  }

  prepareValueForUpdate(field, value) {
    return this.getGrouperFieldType(field).prepareValueForUpdate(field, value)
  }

  prepareValueForPaste(field, value) {
    return this.getGrouperFieldType(field).prepareValueForPaste(field, value)
  }

  getCompatibleFilterFieldType(field) {
    return this.getGrouperFieldType(field)
  }
}

export class PremiumFormulaFieldType extends FormulaFieldType {
  getAdditionalFormInputComponents() {
    return [FormulaFieldAI]
  }
}
